import { useState, type FC } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBan, faEdit, faLocationDot, faPlus } from "@fortawesome/free-solid-svg-icons";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { OFFER_CREATE_PATH } from "@routes/path";
import OfferAssesmentModalForm from "@components/ui/offer-assesment/form/Modal";
import { ApplicantReviewModal } from "@components/ui/dashboard/ApplicantReview/ApplicantReviewModal";
import {
  OfferApplicantStatus,
  POST_ACCEPTED_FINAL_REVIEW_STATUSES,
  POST_CONTRACT_SENT_STATUSES,
  POST_OFFER_ACCEPTED_BY_CANDIDATE_STATUSES,
  POST_OFFER_SENT_STATUSES,
  POST_SHORTLIST_STATUSES,
  POST_SHORTLISTED_BY_HR_QUESTIONNAIRE_STATUSES,
  SHORTLISTED_BY_TECHNICAL_QUESTIONNAIRE,
} from "./../../../../types/OfferApplicantStatus.interface";
import { useLanguage } from '../../../../contexts/LanguageContext';
import translations from './translations.json';
import metricTranslations from './metrics.translations.json';
type SupportedLanguage = keyof typeof translations;

export interface JobCardItemProps {
  id: number;
  uuid: string;
  title: string;
  location: string;
  status: string;
  questionsCount: number;
  technicalQuestionsCount: number;
  applicants: number;
  shortlisted: number;
  answeredQuestions: number;
  assessmentRejected: number;
  assessmentValidated: number;
  technicalAssessmentRejected: number;
  technicalAssessmentValidated: number;
  underFinalReview: number;
  acceptedAfterFinalReview: number;
  rejectedAfterFinalReview: number;
  offerSent: number;
  rejectedByCandidate: number;
  acceptedByCandidate: number;
  contractSent: number;
  hired: number;
}

export interface JobCardProps {
  item: JobCardItemProps;
  formBuilderAccessToken?: string;
  defaultAssesmentValue?: any;
  loadingStatus?: boolean;
  onRefetch?: () => void;
  onChangeStatus?: (offerId: number, newStatus: 'OPEN' | 'CLOSED') => void;
}

const DARK_CARD_BG = '#121824';
const DARK_BORDER = '#1e2638';
const METRIC_BG = '#161d2b';
const STATUS_FILTERS: Partial<Record<OfferApplicantStatus, OfferApplicantStatus[]>> = {
  [OfferApplicantStatus.SHORTLISTED_BY_IA]: POST_SHORTLIST_STATUSES,
  [OfferApplicantStatus.SHORTLISTED_BY_HR_QUESTIONNAIRE]: POST_SHORTLISTED_BY_HR_QUESTIONNAIRE_STATUSES,
  [OfferApplicantStatus.SHORTLISTED_BY_TECHNICAL_QUESTIONNAIRE]: SHORTLISTED_BY_TECHNICAL_QUESTIONNAIRE,
  [OfferApplicantStatus.ACCEPTED_AFTER_FINAL_REVIEW]: POST_ACCEPTED_FINAL_REVIEW_STATUSES,
  [OfferApplicantStatus.OFFER_SENT]: POST_OFFER_SENT_STATUSES,
  [OfferApplicantStatus.OFFER_ACCEPTED_BY_CANDIDATE]: POST_OFFER_ACCEPTED_BY_CANDIDATE_STATUSES,
  [OfferApplicantStatus.CONTRACT_SENT]: POST_CONTRACT_SENT_STATUSES,
};

interface JobMetric {
  value: number;
  label: string;
  status?: OfferApplicantStatus;
  hoverColor?: string;
}

const JobCard: FC<JobCardProps> = ({
  item,
  formBuilderAccessToken = "",
  onRefetch,
  onChangeStatus,
  loadingStatus
}) => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const content = translations[language as SupportedLanguage] || translations.en;
  const metrics = metricTranslations[language as SupportedLanguage] || metricTranslations.en;

  const [openAssesmentForm, setOpenAssesmentForm] = useState<boolean>(false);
  const [openValidatedCandidates, setOpenValidatedCandidates] = useState<boolean>(false);
  const [filterStatusCandidate, setFilterStatusCandidate] = useState<string[]>(
    Object.values(OfferApplicantStatus)
  );

  const jobMetrics: JobMetric[] = [
    { value: item.applicants, label: content.applicants },
    {
      value: item.shortlisted,
      label: content.shortlisted,
      status: OfferApplicantStatus.SHORTLISTED_BY_IA,
      hoverColor: "#3b82f6",
    },
    { value: item.answeredQuestions, label: metrics.answered },
    {
      value: item.assessmentRejected,
      label: metrics.generalRejected,
      status: OfferApplicantStatus.REJECTED_BY_HR_QUESTIONNAIRE,
      hoverColor: "rgb(174, 33, 33)",
    },
    {
      value: item.assessmentValidated,
      label: metrics.generalValidated,
      status: OfferApplicantStatus.SHORTLISTED_BY_HR_QUESTIONNAIRE,
      hoverColor: "#3b82f6",
    },
    {
      value: item.technicalAssessmentRejected,
      label: metrics.technicalRejected,
      status: OfferApplicantStatus.REJECTED_BY_TECHNICAL_QUESTIONNAIRE,
      hoverColor: "rgb(174, 33, 33)",
    },
    {
      value: item.technicalAssessmentValidated,
      label: content.validated,
      status: OfferApplicantStatus.SHORTLISTED_BY_TECHNICAL_QUESTIONNAIRE,
      hoverColor: "#3b82f6",
    },
    {
      value: item.underFinalReview,
      label: metrics.underFinalReview,
      status: OfferApplicantStatus.UNDER_FINAL_REVIEW,
    },
    {
      value: item.rejectedAfterFinalReview,
      label: metrics.rejectedAfterFinalReview,
      status: OfferApplicantStatus.REJECTED_AFTER_FINAL_REVIEW,
      hoverColor: "rgb(174, 33, 33)",
    },
    {
      value: item.acceptedAfterFinalReview,
      label: metrics.acceptedAfterFinalReview,
      status: OfferApplicantStatus.ACCEPTED_AFTER_FINAL_REVIEW,
      hoverColor: "#3b82f6",
    },
    {
      value: item.offerSent,
      label: metrics.offerSent,
      status: OfferApplicantStatus.OFFER_SENT,
    },
    {
      value: item.rejectedByCandidate,
      label: metrics.offerRejectedByCandidate,
      status: OfferApplicantStatus.OFFER_REJECTED_BY_CANDIDATE,
      hoverColor: "rgb(174, 33, 33)",
    },
    {
      value: item.acceptedByCandidate,
      label: metrics.offerAcceptedByCandidate,
      status: OfferApplicantStatus.OFFER_ACCEPTED_BY_CANDIDATE,
      hoverColor: "#3b82f6",
    },
    {
      value: item.contractSent,
      label: metrics.contractSent,
      status: OfferApplicantStatus.CONTRACT_SENT,
    },
    {
      value: item.hired,
      label: metrics.hired,
      status: OfferApplicantStatus.HIRED,
      hoverColor: "#10b981",
    },
  ];

  const handleGoToEdit = () => {
    navigate(OFFER_CREATE_PATH, { state: { offerId: item.id } });
  };

  const handleGoToAssesmentForm = async () => {
    setOpenAssesmentForm(true);
  };

  const handleViewCandidateByStatus = (status: OfferApplicantStatus) => {
    setFilterStatusCandidate(STATUS_FILTERS[status] ?? [status]);
    setOpenValidatedCandidates(true);
  };

  const handleChangeJobStatus = () => {
    onChangeStatus && onChangeStatus(item.id, item.status === "OPEN" ? "CLOSED" : "OPEN");
  };

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: "16px",
        borderColor: DARK_BORDER,
        backgroundColor: DARK_CARD_BG,
        boxShadow: "none",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <CardContent
        sx={{
          padding: "24px !important",
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
        }}
      >
        {/* Header Section: Title & Subtitle on left, Badge + Edit + Questions on right */}
        <Box
          sx={{
            display: "flex",
            justify: "space-between",
            alignItems: "flex-start",
            gap: 2,
            mb: 3,
            flexWrap: "wrap"
          }}
        >
          {/* Title and Location */}
          <Box sx={{ flex: 1, minWidth: "220px" }}>
            <Typography
              variant="h5"
              sx={{ fontWeight: 700, color: "#ffffff", mb: 0.5, fontSize: "1.25rem" }}
            >
              {item.title}
            </Typography>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                color: "#8c94a4",
              }}
            >
              <FontAwesomeIcon icon={faLocationDot} style={{ fontSize: "13px" }} />
              <Typography variant="body2" sx={{ fontSize: "0.85rem", color: "#8c94a4" }}>
                {item.location}
              </Typography>
            </Box>
          </Box>

          {/* Badge + Edit & Questions Buttons (Top Right) */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
            <Chip
              label={item.status === "OPEN" ? content.open : content.closed}
              size="small"
              sx={{
                bgcolor: item.status === "OPEN" ? "rgba(16, 185, 129, 0.15)" : "rgba(239, 68, 68, 0.15)",
                color: item.status === "OPEN" ? "#10b981" : "#ef4444",
                border: `1px solid ${item.status === "OPEN" ? "rgba(16, 185, 129, 0.3)" : "rgba(239, 68, 68, 0.3)"}`,
                fontWeight: 700,
                borderRadius: "20px",
                height: "28px",
                px: 1,
                fontSize: "0.75rem",
                textTransform: "uppercase"
              }}
            />

            <Button
              onClick={handleGoToEdit}
              variant="outlined"
              startIcon={<FontAwesomeIcon icon={faEdit} style={{ fontSize: "13px" }} />}
              sx={{
                color: "#ffffff",
                borderColor: DARK_BORDER,
                backgroundColor: "rgba(255, 255, 255, 0.03)",
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "8px",
                px: 2,
                py: 0.75,
                "&:hover": {
                  borderColor: "#3b82f6",
                  backgroundColor: "rgba(59, 130, 246, 0.08)",
                },
              }}
            >
              {content.edit}
            </Button>

            <Button
              onClick={handleGoToAssesmentForm}
              variant="contained"
              startIcon={<FontAwesomeIcon icon={faPlus} style={{ fontSize: "13px" }} />}
              sx={{
                bgcolor: "rgba(255, 255, 255, 0.08)",
                color: "#ffffff",
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "8px",
                px: 2,
                py: 0.75,
                boxShadow: "none",
                "&:hover": {
                  bgcolor: "rgba(255, 255, 255, 0.15)",
                  boxShadow: "none",
                },
              }}
            >
              {`${content.manage} (${item.questionsCount} ${content.generalShort} - ${item.technicalQuestionsCount} ${content.technicalShort})`}
            </Button>
          </Box>
        </Box>

        {/* Metrics Grid */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(3, 1fr)", md: "repeat(5, 1fr)" },
            gap: 1.5,
            my: 1,
          }}
        >
          {jobMetrics.map(({ value, label, status, hoverColor }) => (
            <Box
              key={label}
              onClick={status ? () => handleViewCandidateByStatus(status) : undefined}
              sx={{
                p: 1.5,
                border: `1px solid ${DARK_BORDER}`,
                borderRadius: "10px",
                bgcolor: METRIC_BG,
                ...(status && {
                  cursor: "pointer",
                  transition: "border-color 0.2s",
                  "&:hover": { borderColor: hoverColor ?? "#3b82f6" },
                }),
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#ffffff", lineHeight: 1 }}>
                {value}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: "#8c94a4", mt: 0.75, display: "block", fontSize: "0.75rem", lineHeight: 1.2 }}
              >
                {label}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Footer: Close / Reopen Action Button aligned bottom-right */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: "auto", pt: 2 }}>
          <Button
            variant="outlined"
            onClick={handleChangeJobStatus}
            disabled={loadingStatus}
            startIcon={<FontAwesomeIcon icon={faBan} style={{ fontSize: "13px" }} />}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "8px",
              px: 2.5,
              py: 0.8,
              color: item.status === "OPEN" ? "#ef4444" : "#10b981",
              borderColor: item.status === "OPEN" ? "rgba(239, 68, 68, 0.3)" : "rgba(16, 185, 129, 0.3)",
              backgroundColor: item.status === "OPEN" ? "rgba(239, 68, 68, 0.08)" : "rgba(16, 185, 129, 0.08)",
              "&:hover": {
                backgroundColor: item.status === "OPEN" ? "rgba(239, 68, 68, 0.18)" : "rgba(16, 185, 129, 0.18)",
                borderColor: item.status === "OPEN" ? "#ef4444" : "#10b981",
              }
            }}
          >
            {loadingStatus && content.loading}
            {!loadingStatus && (item.status === "OPEN" ? content.close : content.reopen)}
          </Button>
        </Box>
      </CardContent>
      {openAssesmentForm && (
        <OfferAssesmentModalForm 
          open={openAssesmentForm}
          onClose={() => setOpenAssesmentForm(false)}
          formBuilderAccessToken={formBuilderAccessToken}
          offer={item}
          onRefetch={onRefetch}
        />
      )}
      <ApplicantReviewModal
        open={openValidatedCandidates}
        onClose={() => setOpenValidatedCandidates(false)}
        applicantStatus={filterStatusCandidate.join(',')}
        offerId={item.id}
      />
    </Card>
  );
};

export default JobCard;